import cx from 'classnames';
import {useCallback, useMemo} from 'react';

type Props = {
    checked: boolean;
    name: string;
    label?: string;
    // A label resolves its target through document.getElementById, which is global — a form field name is only
    // unique inside its own form, so several forms on one page need the prefix to keep their ids apart.
    idPrefix?: string;
    disabled?: boolean;
    size?: 'normal' | 'small'
    className?: string;
    // TODO: inverse values to make it working predictable
    // label is placed on the left when "right" is used and vice-versa
    labelPlacement?: 'left' | 'right';
    onChange?: (value: boolean) => void;
};

export type ToggleProps = Props;

export const Toggle: React.FC<Props> = ({
    name,
    checked: checkedProp,
    label,
    idPrefix,
    disabled,
    className,
    onChange,
    size = 'normal',
    labelPlacement = 'left',
}: Props) => {
    const id = useMemo(() => (idPrefix ? `toggle-${idPrefix}-${name}` : `toggle-${name}`), [idPrefix, name]);
    const checked = useMemo(() => Boolean(checkedProp), [checkedProp]);
    const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        onChange?.(event.target.checked);
    }, [onChange]);

    return (
        <label
            htmlFor={id}
            className={cx(className, 'vs-toggle tw-relative tw-inline-flex tw-items-center tw-cursor-pointer', {
                'tw-text-default-dimmed': disabled,
            })}
        >
            {onChange && <input
                id={id}
                disabled={disabled}
                type='checkbox'
                className='tw-absolute tw-opacity-0'
                checked={checked}
                onChange={handleChange}
            />}
            <span
                className={cx('tw-relative tw-flex-shrink-0 tw-transition-all',
                    'tw-duration-500 tw-rounded-3xl tw-border-2 tw-bg-opacity-100', {
                        'tw-bg-primary tw-border-primary': checked,
                        'tw-border-default': !checked,
                        'tw-bg-default tw-border-default': disabled,
                        'tw-order-2': labelPlacement === 'right',
                        'tw-w-9 tw-h-5': size === 'small',
                        'tw-w-14 tw-h-7': size === 'normal',
                    })}
            >
                <span
                    className={cx('tw-absolute tw-top-0.65 tw-left-0.65',
                        'tw-transition-all tw-duration-500 tw-rounded-full tw-bg-light tw-border-dark', {
                            'tw-transform tw-border-none': checked,
                            'tw-translate-x-4': checked && size === 'small',
                            'tw-translate-x-7': checked && size === 'normal',
                            'tw-w-3 tw-h-3 tw-border-2': size === 'small',
                            'tw-w-5 tw-h-5 tw-border-4': size === 'normal',
                        })}
                />
            </span>
            {label && <span className={labelPlacement === 'right' ? 'tw-mr-2' : 'tw-ml-2'}>{label}</span>}
        </label>
    );
};